import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { tasksAPI, teamsAPI, labelsAPI, usersAPI } from '../services/api';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { 
  CheckSquare, 
  Clock, 
  AlertTriangle, 
  CheckCircle, 
  Users,
  Tag,
  ArrowRight,
  Plus,
  Timer,
  Camera
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import PhotoJobWizard from '../components/PhotoJobWizard';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';

const Dashboard = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [tasks, setTasks] = useState([]);
  const [teams, setTeams] = useState([]);
  const [labels, setLabels] = useState([]);
  const [loading, setLoading] = useState(true);
  
  // Onboarding
  const [showOnboarding, setShowOnboarding] = useState(false);
  const [teamName, setTeamName] = useState('');
  const [showPhotoWizard, setShowPhotoWizard] = useState(false);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [tasksRes, teamsRes, labelsRes, onboardingRes] = await Promise.all([
          tasksAPI.list({ archived: false }),
          teamsAPI.list(),
          labelsAPI.list(),
          usersAPI.onboardingStatus()
        ]);
        setTasks(tasksRes.data);
        setTeams(teamsRes.data);
        setLabels(labelsRes.data);
        
        // Show onboarding if user has no teams
        if (!onboardingRes.data.has_teams) {
          setShowOnboarding(true);
        }
      } catch (error) {
        console.error('Error fetching dashboard data:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  const handleCreateTeam = async () => {
    if (!teamName.trim()) {
      toast.error('Team name is required');
      return;
    }
    try {
      await teamsAPI.create({ name: teamName });
      toast.success('Team created!');
      setShowOnboarding(false);
      setTeamName('');
      // Refresh data
      const [tasksRes, teamsRes] = await Promise.all([
        tasksAPI.list({ archived: false }),
        teamsAPI.list()
      ]);
      setTasks(tasksRes.data);
      setTeams(teamsRes.data);
    } catch (error) {
      toast.error('Failed to create team');
    }
  };

  const stats = {
    open: tasks.filter(t => t.status === 'OPEN').length,
    inProgress: tasks.filter(t => t.status === 'IN_PROGRESS').length,
    blocked: tasks.filter(t => t.status === 'BLOCKED').length,
    done: tasks.filter(t => t.status === 'DONE').length,
  };

  // Hours estimate for active tasks assigned to user
  const myActiveTasks = tasks.filter(
    t => t.assignee_id === user?.user_id && t.status !== 'DONE'
  );
  const myHoursEstimate = myActiveTasks.reduce((sum, t) => sum + (t.estimate || 0), 0);

  const myOpenTasks = tasks.filter(t => t.assignee_id === user?.user_id && t.status !== 'DONE').slice(0, 5);

  const statusColors = {
    OPEN: 'bg-blue-100 text-blue-800',
    IN_PROGRESS: 'bg-amber-100 text-amber-800',
    WAITING_ON_PARTS: 'bg-purple-100 text-purple-800',
    BLOCKED: 'bg-red-100 text-red-800',
    DONE: 'bg-emerald-100 text-emerald-800'
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-8" data-testid="dashboard-page">
      {/* Welcome Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-slate-900 font-heading">
            Welcome back, {user?.name?.split(' ')[0]}
          </h1>
          <p className="mt-1 text-slate-600">Here's an overview of your projects.</p>
        </div>
        <Button className="bg-amber-500 hover:bg-amber-600 text-white shadow-md" onClick={() => setShowPhotoWizard(true)} data-testid="dashboard-photo-job-btn">
          <Camera className="mr-2 h-5 w-5" />
          New Job from Photo
        </Button>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-6">
        <Card className="card-hover">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-slate-600">Open</p>
                <p className="text-3xl font-bold text-slate-900 mt-1">{stats.open}</p>
              </div>
              <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center">
                <CheckSquare className="h-6 w-6 text-blue-600" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="card-hover">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-slate-600">In Progress</p>
                <p className="text-3xl font-bold text-slate-900 mt-1">{stats.inProgress}</p>
              </div>
              <div className="w-12 h-12 bg-amber-100 rounded-lg flex items-center justify-center">
                <Clock className="h-6 w-6 text-amber-600" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="card-hover">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-slate-600">Blocked</p>
                <p className="text-3xl font-bold text-slate-900 mt-1">{stats.blocked}</p>
              </div>
              <div className="w-12 h-12 bg-red-100 rounded-lg flex items-center justify-center">
                <AlertTriangle className="h-6 w-6 text-red-600" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="card-hover">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-slate-600">Completed</p>
                <p className="text-3xl font-bold text-slate-900 mt-1">{stats.done}</p>
              </div>
              <div className="w-12 h-12 bg-emerald-100 rounded-lg flex items-center justify-center">
                <CheckCircle className="h-6 w-6 text-emerald-600" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="card-hover">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-slate-600">My Hours Est.</p>
                <p className="text-3xl font-bold text-slate-900 mt-1">{myHoursEstimate}h</p>
              </div>
              <div className="w-12 h-12 bg-indigo-100 rounded-lg flex items-center justify-center">
                <Timer className="h-6 w-6 text-indigo-600" />
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Content Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        {/* My Open Tasks */}
        <Card className="lg:col-span-2">
          <CardHeader className="flex flex-row items-center justify-between">
            <CardTitle className="font-heading">My Open Tasks</CardTitle>
            <Button variant="ghost" size="sm" onClick={() => navigate('/dashboard/tasks')}>
              View All <ArrowRight className="ml-1 h-4 w-4" />
            </Button>
          </CardHeader>
          <CardContent>
            {myOpenTasks.length === 0 ? (
              <div className="text-center py-8 text-slate-500">
                <CheckSquare className="h-12 w-12 mx-auto mb-3 text-slate-300" />
                <p>No open tasks assigned to you.</p>
                <Button 
                  variant="outline" 
                  size="sm" 
                  className="mt-4"
                  onClick={() => navigate('/dashboard/tasks')}
                >
                  <Plus className="mr-2 h-4 w-4" /> Create Task
                </Button>
              </div>
            ) : (
              <div className="space-y-3">
                {myOpenTasks.map((task) => (
                  <div
                    key={task.task_id}
                    onClick={() => navigate(`/dashboard/tasks?task=${task.task_id}`)}
                    className="flex items-center justify-between p-3 rounded-lg border border-slate-200 hover:border-indigo-300 hover:bg-slate-50 cursor-pointer transition-all"
                  >
                    <div className="flex-1 min-w-0">
                      <p className="font-medium text-slate-900 truncate">{task.title}</p>
                      <div className="flex items-center gap-3 mt-1 text-sm text-slate-500">
                        {task.due_date && (
                          <span className="flex items-center gap-1">
                            <Clock className="h-3 w-3" />
                            {new Date(task.due_date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                          </span>
                        )}
                        {task.assignee_name && (
                          <span className="truncate">{task.assignee_name}</span>
                        )}
                      </div>
                      {task.label_details?.length > 0 && (
                        <div className="flex flex-wrap gap-1 mt-1">
                          {task.label_details.map(label => (
                            <Badge
                              key={label.label_id}
                              style={{ backgroundColor: label.color + '20', color: label.color }}
                              className="text-xs px-1.5 py-0"
                            >
                              {label.name}
                            </Badge>
                          ))}
                        </div>
                      )}
                    </div>
                    <div className="flex items-center gap-2 ml-3 flex-shrink-0">
                      {task.estimate && (
                        <span className="text-xs text-slate-500">{task.estimate}h</span>
                      )}
                      <Badge className={statusColors[task.status]}>
                        {task.status === 'WAITING_ON_PARTS' ? 'WAITING ON PARTS' : task.status.replace('_', ' ')}
                      </Badge>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Quick Stats */}
        <div className="space-y-6">
          {/* Teams */}
          <Card>
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle className="font-heading text-lg">Teams</CardTitle>
              <Button variant="ghost" size="sm" onClick={() => navigate('/dashboard/teams')}>
                <ArrowRight className="h-4 w-4" />
              </Button>
            </CardHeader>
            <CardContent>
              {teams.length === 0 ? (
                <div className="text-center py-4 text-slate-500">
                  <Users className="h-8 w-8 mx-auto mb-2 text-slate-300" />
                  <p className="text-sm">No teams yet</p>
                </div>
              ) : (
                <div className="space-y-2">
                  {teams.slice(0, 3).map((team) => (
                    <div 
                      key={team.group_id}
                      className="flex items-center justify-between p-2 rounded-md hover:bg-slate-50"
                    >
                      <span className="font-medium text-sm">{team.name}</span>
                      <Badge variant="secondary">{team.members?.length || 0} members</Badge>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>

          {/* Labels */}
          <Card>
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle className="font-heading text-lg">Labels</CardTitle>
              <Button variant="ghost" size="sm" onClick={() => navigate('/dashboard/settings')}>
                <ArrowRight className="h-4 w-4" />
              </Button>
            </CardHeader>
            <CardContent>
              {labels.length === 0 ? (
                <div className="text-center py-4 text-slate-500">
                  <Tag className="h-8 w-8 mx-auto mb-2 text-slate-300" />
                  <p className="text-sm">No labels yet</p>
                </div>
              ) : (
                <div className="flex flex-wrap gap-2">
                  {labels.slice(0, 8).map((label) => (
                    <Badge 
                      key={label.label_id}
                      style={{ backgroundColor: label.color + '20', color: label.color, borderColor: label.color }}
                      className="border"
                    >
                      {label.name}
                    </Badge>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Photo Job Wizard */}
      <PhotoJobWizard
        open={showPhotoWizard}
        onClose={() => setShowPhotoWizard(false)}
        onTaskCreated={() => navigate('/dashboard/tasks')}
      />

      {/* Onboarding Dialog */}
      <Dialog open={showOnboarding} onOpenChange={() => {}}>
        <DialogContent className="sm:max-w-md" onInteractOutside={(e) => e.preventDefault()}>
          <DialogHeader>
            <DialogTitle className="font-heading">Welcome to TaskMaster!</DialogTitle>
            <DialogDescription>
              Create a team to get started. All your tasks will belong to a team so your crew can collaborate.
            </DialogDescription>
          </DialogHeader>
          <div className="py-4 space-y-4">
            <div>
              <Label>Team Name</Label>
              <Input
                value={teamName}
                onChange={(e) => setTeamName(e.target.value)}
                placeholder="e.g., My Workshop, Smith Electrical"
                className="mt-2"
                data-testid="onboarding-team-name"
              />
            </div>
            <p className="text-sm text-slate-500">
              Have an invite token? Go to <button onClick={() => { setShowOnboarding(false); navigate('/dashboard/teams'); }} className="text-indigo-600 hover:underline">Teams</button> to join an existing team.
            </p>
          </div>
          <DialogFooter>
            <Button onClick={handleCreateTeam} data-testid="onboarding-create-team-btn">
              Create Team & Get Started
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Dashboard;
